import { DynamoDBClient, QueryCommand, ScanCommand } from "@aws-sdk/client-dynamodb";
import { marshall, unmarshall } from "@aws-sdk/util-dynamodb";
import { apiGetRequest, apiPostRequest } from "./requests";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export interface EnrolledStudent {
  courseId: string;
  courseName?: string;
  userId: string;
  userName: string;
  userEmail: string;
  appVersion: string;
  downloadedAt: number;
  status: 'Active' | 'Completed' | 'Inactive';
  lastActive: string;
  lastAttemptDate?: string;
  lastAttemptTimestamp?: number;
  quizAttemptsCount?: number;
  avgScorePercentage?: number;
  passedCount?: number;
  passRate?: number;
  highestScore?: number;
}

export interface QuizQuestionResponseItem {
  questionId: string;
  questionText: string;
  selectedAnswer: string | null;
  correctAnswer: string;
  isCorrect: boolean;
}

export interface StudentQuizAttempt {
  attemptId: string;
  courseId: string;
  courseName?: string;
  userId: string;
  userName: string;
  userEmail: string;
  quizId: string;
  quizTitle: string;
  chapterName: string;
  score: number;
  totalQuestions: number;
  percentage: number;
  passed: boolean;
  mode?: 'sequential' | 'sequence' | 'jumbled' | 'random' | string;
  timeSpentSeconds: number;
  timestamp: number;
  dateString: string;
  isoDate?: string;
  attemptNumber?: number;
  questionResponses?: QuizQuestionResponseItem[];
}

export interface PollOptionDistribution {
  option: string;
  votes: number;
  percentage: number;
  isTopChoice: boolean;
}

export interface QuestionDifficultyItem {
  questionId: string;
  questionText: string;
  totalAttempts: number;
  wrongAttempts: number;
  errorRate: number;
  correctAnswer?: string;
  commonWrongAnswer?: string;
  quizId?: string;
  quizTitle?: string;
  chapterName?: string;
  isSurvey?: boolean;
  allWrongChoices?: { answer: string; count: number; percentage: number }[];
  pollDistribution?: PollOptionDistribution[];
}

export interface CourseAnalyticsSummary {
  totalEnrolled: number;
  activeStudents: number;
  completedStudents: number;
  totalAttempts: number;
  averageScore: number;
  overallPassRate: number;
  scoreDistribution: { range: string; count: number; color: string }[];
  quizBreakdown: { quizId: string; title: string; chapter: string; attempts: number; passRate: number; avgScore: number }[];
  activityTimeline: { date: string; attempts: number; avgScore: number }[];
  monthlyIntake: { month: string; enrolled: number }[];
  courseComparison?: { courseId: string; courseName: string; enrolled: number; attempts: number; avgScore: number; passRate: number }[];
  masteryBreakdown?: { label: string; count: number; percentage: number; color: string }[];
  questionDifficulty?: QuestionDifficultyItem[];
}

const TABLE_ENROLLMENTS = 'xnav-v5-dev-course-enrollments';
const TABLE_ATTEMPTS = 'xnav-v5-dev-user-quiz-attempts';

export async function fetchCourseEnrollments(
  dynamo: DynamoDBClient | undefined,
  courseId: string
): Promise<{ students: EnrolledStudent[]; attempts: StudentQuizAttempt[] }> {
  let enrollments: EnrolledStudent[] = [];
  let attempts: StudentQuizAttempt[] = [];

  const isGlobal = !courseId || courseId === 'global';

  // 1. Query or Scan course enrollments
  if (dynamo) {
    try {
      if (isGlobal) {
        const scanCmd = new ScanCommand({ TableName: TABLE_ENROLLMENTS });
        const res = await dynamo.send(scanCmd);
        if (res.Items && res.Items.length > 0) {
          enrollments = res.Items.map(i => unmarshall(i) as EnrolledStudent);
        }
      } else {
        const enrollCmd = new QueryCommand({
          TableName: TABLE_ENROLLMENTS,
          KeyConditionExpression: 'courseId = :courseId',
          ExpressionAttributeValues: marshall({ ':courseId': courseId })
        });
        const res = await dynamo.send(enrollCmd);
        if (res.Items && res.Items.length > 0) {
          enrollments = res.Items.map(i => unmarshall(i) as EnrolledStudent);
        }
      }
    } catch (e) {
      console.warn("DynamoDB enrollment query error:", e);
    }
  }

  // 2. Fetch attempts
  if (dynamo) {
    try {
      if (isGlobal) {
        const scanAttCmd = new ScanCommand({ TableName: TABLE_ATTEMPTS });
        const res = await dynamo.send(scanAttCmd);
        if (res.Items && res.Items.length > 0) {
          attempts = res.Items.map(i => unmarshall(i) as StudentQuizAttempt);
        }
      } else {
        const attCmd = new QueryCommand({
          TableName: TABLE_ATTEMPTS,
          KeyConditionExpression: 'courseId = :courseId',
          ExpressionAttributeValues: marshall({ ':courseId': courseId })
        });
        const res = await dynamo.send(attCmd);
        if (res.Items && res.Items.length > 0) {
          attempts = res.Items.map(i => unmarshall(i) as StudentQuizAttempt);
        }
      }
    } catch (e) {
      console.warn("DynamoDB quiz attempts query error:", e);
    }
  }

  // Fallback to HTTP API if enrollments empty
  if (enrollments.length === 0 && !isGlobal) {
    try {
      const apiEnrollments = await apiGetRequest<undefined, any[]>(`/analytics/enrollments/${encodeURIComponent(courseId)}`);
      if (Array.isArray(apiEnrollments) && apiEnrollments.length > 0) {
        enrollments = apiEnrollments;
      }
    } catch (e) {
      console.log("No HTTP API enrollments endpoint, using loaded data");
    }
  }

  // 3. Compute student statistics directly from quiz attempts
  const enrichedStudents: EnrolledStudent[] = enrollments.map(s => {
    const studentAttempts = attempts.filter(a => a.userId === s.userId && (isGlobal || a.courseId === s.courseId));
    const totalAtt = studentAttempts.length;
    const passedAtt = studentAttempts.filter(a => a.passed || a.percentage >= 70).length;
    const avgScore = totalAtt > 0
      ? Math.round(studentAttempts.reduce((sum, a) => sum + (a.percentage || 0), 0) / totalAtt)
      : 0;
    const passRate = totalAtt > 0 ? Math.round((passedAtt / totalAtt) * 100) : 0;
    const highest = totalAtt > 0 ? Math.max(...studentAttempts.map(a => a.percentage || 0)) : 0;

    // Calculate last attempt date/time
    let lastAttemptDate: string | undefined = undefined;
    let lastAttemptTimestamp: number | undefined = undefined;
    if (totalAtt > 0) {
      const sorted = [...studentAttempts].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
      const latest = sorted[0];
      lastAttemptTimestamp = latest.timestamp;
      lastAttemptDate = latest.dateString || latest.isoDate || (latest.timestamp ? new Date(latest.timestamp).toISOString() : undefined);
    }

    return {
      ...s,
      quizAttemptsCount: totalAtt,
      avgScorePercentage: avgScore,
      passedCount: passedAtt,
      passRate: passRate,
      highestScore: highest,
      lastAttemptDate,
      lastAttemptTimestamp
    };
  });

  return { students: enrichedStudents, attempts };
}

export async function fetchStudentQuizHistory(
  dynamo: DynamoDBClient | undefined,
  userId: string
): Promise<StudentQuizAttempt[]> {
  if (dynamo && userId) {
    try {
      const cmd = new QueryCommand({
        TableName: TABLE_ATTEMPTS,
        IndexName: 'GSI_User_History',
        KeyConditionExpression: 'userId = :userId',
        ExpressionAttributeValues: marshall({ ':userId': userId })
      });
      const res = await dynamo.send(cmd);
      if (res.Items && res.Items.length > 0) {
        const list = res.Items.map(i => {
          const item = unmarshall(i) as StudentQuizAttempt;
          const totalQ = item.totalQuestions && item.totalQuestions > 0 ? item.totalQuestions : 20;
          const pct = item.percentage !== undefined && item.percentage !== null
            ? item.percentage
            : Math.round(((item.score || 0) / totalQ) * 100);
          return {
            ...item,
            totalQuestions: totalQ,
            percentage: pct,
            passed: item.passed !== undefined ? item.passed : pct >= 70
          };
        });
        return list.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
      }
    } catch (e) {
      console.error("Error querying GSI_User_History:", e);
    }
  }

  try {
    const res = await apiGetRequest<undefined, StudentQuizAttempt[]>(`/analytics/student-history/${encodeURIComponent(userId)}`);
    if (Array.isArray(res)) return res;
  } catch (e) {
    console.warn("HTTP student history fetch failed:", e);
  }

  return [];
}

export async function fetchQuizPerformance(
  dynamo: DynamoDBClient | undefined,
  quizId: string
): Promise<{ totalAttempts: number; passRate: number; avgPercentage: number; attempts: StudentQuizAttempt[] }> {
  if (dynamo && quizId) {
    try {
      const cmd = new QueryCommand({
        TableName: TABLE_ATTEMPTS,
        IndexName: 'GSI_Quiz_Performance',
        KeyConditionExpression: 'quizId = :quizId',
        ExpressionAttributeValues: marshall({ ':quizId': quizId })
      });
      const res = await dynamo.send(cmd);
      if (res.Items) {
        const attempts = res.Items.map(i => unmarshall(i) as StudentQuizAttempt);
        const total = attempts.length;
        const passed = attempts.filter(a => a.percentage >= 70).length;
        const avg = total > 0 ? Math.round(attempts.reduce((sum, a) => sum + (a.percentage || 0), 0) / total) : 0;
        const rate = total > 0 ? Math.round((passed / total) * 100) : 0;
        return { totalAttempts: total, passRate: rate, avgPercentage: avg, attempts };
      }
    } catch (e) {
      console.error("Error querying GSI_Quiz_Performance:", e);
    }
  }

  return { totalAttempts: 0, passRate: 0, avgPercentage: 0, attempts: [] };
}

export function computeCourseSummary(
  students: EnrolledStudent[],
  attempts: StudentQuizAttempt[],
  courseMap?: Record<string, string>
): CourseAnalyticsSummary {
  const totalEnrolled = students.length;
  const activeStudents = students.filter(s => s.status === 'Active').length;
  const completedStudents = students.filter(s => s.status === 'Completed').length;
  const totalAttempts = attempts.length;

  const avgScore = totalAttempts > 0
    ? Math.round(attempts.reduce((sum, a) => sum + (a.percentage || 0), 0) / totalAttempts)
    : 0;

  const totalPassed = attempts.filter(a => a.passed || a.percentage >= 70).length;
  const overallPassRate = totalAttempts > 0 ? Math.round((totalPassed / totalAttempts) * 100) : 0;

  // Score distribution buckets
  const buckets = [
    { range: '0-20%', min: 0, max: 20, count: 0, color: '#f43f5e' },
    { range: '21-40%', min: 21, max: 40, count: 0, color: '#f59e0b' },
    { range: '41-60%', min: 41, max: 60, count: 0, color: '#0284c7' },
    { range: '61-80%', min: 61, max: 80, count: 0, color: '#0ea5e9' },
    { range: '81-100%', min: 81, max: 100, count: 0, color: '#10b981' }
  ];

  attempts.forEach(a => {
    const pct = a.percentage || 0;
    const bucket = buckets.find(b => pct >= b.min && pct <= b.max);
    if (bucket) bucket.count++;
  });

  const scoreDistribution = buckets.map(b => ({ range: b.range, count: b.count, color: b.color }));

  // Mastery breakdown for Donut/Pie chart (Unified Cyan & Emerald Palette)
  const masteryTiers = [
    { label: 'Mastery (90-100%)', count: 0, color: '#10b981' },
    { label: 'Proficient (80-89%)', count: 0, color: '#0ea5e9' },
    { label: 'Passing (70-79%)', count: 0, color: '#0284c7' },
    { label: 'Needs Practice (<70%)', count: 0, color: '#f59e0b' }
  ];

  attempts.forEach(a => {
    const p = a.percentage || 0;
    if (p >= 90) masteryTiers[0].count++;
    else if (p >= 80) masteryTiers[1].count++;
    else if (p >= 70) masteryTiers[2].count++;
    else masteryTiers[3].count++;
  });

  const masteryBreakdown = masteryTiers.map(m => ({
    ...m,
    percentage: totalAttempts > 0 ? Math.round((m.count / totalAttempts) * 100) : 0
  }));

  // Quiz breakdown
  const quizMap: Record<string, { title: string; chapter: string; attempts: StudentQuizAttempt[] }> = {};
  attempts.forEach(a => {
    const key = a.quizId || 'general';
    if (!quizMap[key]) {
      quizMap[key] = {
        title: a.quizTitle || 'Course Quiz',
        chapter: a.chapterName || 'General Chapter',
        attempts: []
      };
    }
    quizMap[key].attempts.push(a);
  });

  const quizBreakdown = Object.entries(quizMap).map(([quizId, data]) => {
    const attCount = data.attempts.length;
    const passCount = data.attempts.filter(a => a.percentage >= 70).length;
    const avg = attCount > 0 ? Math.round(data.attempts.reduce((sum, a) => sum + a.percentage, 0) / attCount) : 0;
    const rate = attCount > 0 ? Math.round((passCount / attCount) * 100) : 0;
    return {
      quizId,
      title: data.title,
      chapter: data.chapter,
      attempts: attCount,
      passRate: rate,
      avgScore: avg
    };
  });

  // Activity Timeline (aggregated by date)
  const timelineMap: Record<string, { total: number; scoreSum: number }> = {};
  attempts.forEach(a => {
    const dateStr = a.dateString ? a.dateString.split('T')[0] : (a.timestamp ? new Date(a.timestamp).toISOString().split('T')[0] : 'Recent');
    if (!timelineMap[dateStr]) {
      timelineMap[dateStr] = { total: 0, scoreSum: 0 };
    }
    timelineMap[dateStr].total++;
    timelineMap[dateStr].scoreSum += (a.percentage || 0);
  });

  const activityTimeline = Object.entries(timelineMap)
    .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
    .slice(-14)
    .map(([date, data]) => ({
      date,
      attempts: data.total,
      avgScore: Math.round(data.scoreSum / data.total)
    }));

  // Per-Course Comparison (for Global View)
  const courseGroup: Record<string, { enrolled: number; attempts: StudentQuizAttempt[] }> = {};
  students.forEach(s => {
    if (!courseGroup[s.courseId]) {
      courseGroup[s.courseId] = { enrolled: 0, attempts: [] };
    }
    courseGroup[s.courseId].enrolled++;
  });

  attempts.forEach(a => {
    if (!courseGroup[a.courseId]) {
      courseGroup[a.courseId] = { enrolled: 0, attempts: [] };
    }
    courseGroup[a.courseId].attempts.push(a);
  });

  const courseComparison = Object.entries(courseGroup).map(([cid, data]) => {
    const attCount = data.attempts.length;
    const passCount = data.attempts.filter(a => a.percentage >= 70).length;
    const avg = attCount > 0 ? Math.round(data.attempts.reduce((sum, a) => sum + a.percentage, 0) / attCount) : 0;
    const rate = attCount > 0 ? Math.round((passCount / attCount) * 100) : 0;
    const cName = courseMap?.[cid] || (cid === 'b29691bf-47a3-480f-84cd-10f510dfc1d1' ? 'MSK KNEE nav 1.0' : cid.slice(0, 10));

    return {
      courseId: cid,
      courseName: cName,
      enrolled: data.enrolled,
      attempts: attCount,
      avgScore: avg,
      passRate: rate
    };
  });

  // Monthly Intake Breakdown (from enrollment downloadedAt)
  const monthMap: Record<string, { month: string; enrolled: number; sortKey: number }> = {};
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  students.forEach(s => {
    if (s.downloadedAt) {
      const d = new Date(s.downloadedAt);
      const mIdx = d.getMonth();
      const year = d.getFullYear();
      const mKey = `${monthNames[mIdx]} ${year}`;
      const sortKey = year * 100 + mIdx;
      if (!monthMap[mKey]) {
        monthMap[mKey] = { month: mKey, enrolled: 0, sortKey };
      }
      monthMap[mKey].enrolled += 1;
    } else {
      const mKey = 'Recent';
      if (!monthMap[mKey]) {
        monthMap[mKey] = { month: mKey, enrolled: 0, sortKey: 999999 };
      }
      monthMap[mKey].enrolled += 1;
    }
  });

  const monthlyIntake = Object.values(monthMap)
    .sort((a, b) => a.sortKey - b.sortKey)
    .map(m => ({
      month: m.month,
      enrolled: m.enrolled
    }));

  const questionDifficulty = computeQuestionDifficulty(attempts);

  return {
    totalEnrolled,
    activeStudents,
    completedStudents,
    totalAttempts,
    averageScore: avgScore,
    overallPassRate,
    scoreDistribution,
    quizBreakdown,
    activityTimeline,
    monthlyIntake,
    courseComparison,
    masteryBreakdown,
    questionDifficulty
  };
}

export function computeQuestionDifficulty(attempts: StudentQuizAttempt[], targetQuizId?: string): QuestionDifficultyItem[] {
  const filteredAttempts = targetQuizId && targetQuizId !== 'all'
    ? attempts.filter(a => a.quizId === targetQuizId)
    : attempts;

  const map: Record<string, {
    questionText: string;
    totalAttempts: number;
    wrongAttempts: number;
    correctAnswer: string;
    wrongOptions: Record<string, number>;
    allResponses: Record<string, number>;
    quizId?: string;
    quizTitle?: string;
    chapterName?: string;
  }> = {};

  filteredAttempts.forEach(att => {
    if (att.questionResponses && Array.isArray(att.questionResponses)) {
      att.questionResponses.forEach(r => {
        const qKey = (r.questionId && r.questionId.trim()) || (r.questionText && r.questionText.trim());
        if (!qKey) return;
        if (!map[qKey]) {
          map[qKey] = {
            questionText: r.questionText || 'Quiz Question',
            totalAttempts: 0,
            wrongAttempts: 0,
            correctAnswer: r.correctAnswer || '',
            wrongOptions: {},
            allResponses: {},
            quizId: att.quizId,
            quizTitle: att.quizTitle || 'Course Assessment Quiz',
            chapterName: att.chapterName || 'General Assessment',
          };
        }
        map[qKey].totalAttempts++;
        if (r.selectedAnswer) {
          const ansStr = String(r.selectedAnswer).trim();
          map[qKey].allResponses[ansStr] = (map[qKey].allResponses[ansStr] || 0) + 1;
        }
        if (!r.isCorrect) {
          map[qKey].wrongAttempts++;
          if (r.selectedAnswer) {
            const ansStr = String(r.selectedAnswer).trim();
            map[qKey].wrongOptions[ansStr] = (map[qKey].wrongOptions[ansStr] || 0) + 1;
          }
        }
      });
    }
  });

  return Object.entries(map).map(([qId, data]) => {
    const errorRate = data.totalAttempts > 0
      ? Math.round((data.wrongAttempts / data.totalAttempts) * 100)
      : 0;

    let commonWrongAnswer: string | undefined = undefined;
    let maxWrongCount = 0;
    const allWrongChoices: { answer: string; count: number; percentage: number }[] = [];

    Object.entries(data.wrongOptions).forEach(([opt, count]) => {
      if (count > maxWrongCount) {
        maxWrongCount = count;
        commonWrongAnswer = opt;
      }
      allWrongChoices.push({
        answer: opt,
        count,
        percentage: data.wrongAttempts > 0 ? Math.round((count / data.wrongAttempts) * 100) : 0
      });
    });

    allWrongChoices.sort((a, b) => b.count - a.count);

    // Survey / Poll Option Distribution
    let maxVotes = 0;
    Object.entries(data.allResponses).forEach(([_, count]) => {
      if (count > maxVotes) maxVotes = count;
    });

    const pollDistribution: PollOptionDistribution[] = Object.entries(data.allResponses).map(([opt, count]) => ({
      option: opt,
      votes: count,
      percentage: data.totalAttempts > 0 ? Math.round((count / data.totalAttempts) * 100) : 0,
      isTopChoice: count === maxVotes && maxVotes > 0
    })).sort((a, b) => b.votes - a.votes);

    const isSurvey = !data.correctAnswer ||
      data.correctAnswer === '-1' ||
      data.correctAnswer.toLowerCase() === 'survey' ||
      data.correctAnswer.toLowerCase() === 'poll' ||
      data.correctAnswer === '';

    return {
      questionId: qId,
      questionText: data.questionText,
      totalAttempts: data.totalAttempts,
      wrongAttempts: data.wrongAttempts,
      errorRate,
      correctAnswer: data.correctAnswer,
      commonWrongAnswer,
      quizId: data.quizId,
      quizTitle: data.quizTitle,
      chapterName: data.chapterName,
      isSurvey,
      allWrongChoices,
      pollDistribution
    };
  }).sort((a, b) => b.errorRate - a.errorRate || b.wrongAttempts - a.wrongAttempts);
}

import html2canvas from "html2canvas";

export async function sendInstructorEmail(payload: {
  recipientEmail: string;
  studentName: string;
  courseName: string;
  subject: string;
  messageBody: string;
  instructorName: string;
}): Promise<{ success: boolean; message: string }> {
  const res = await apiPostRequest<any, { message: string; sentCount?: number }>(
    '/users/email_broadcast',
    {
      subject: payload.subject,
      message: payload.messageBody,
      targetAudience: 'specific',
      targetValues: [payload.recipientEmail],
      senderEmail: 'notifications@xnav.studio',
      includeTeamSignoff: false
    }
  );
  return { success: true, message: res?.message || `Notification sent to ${payload.recipientEmail}` };
}

export async function exportToPDF(
  students: EnrolledStudent[],
  summary: CourseAnalyticsSummary | null,
  courseTitle: string = 'Course',
  withGraphs: boolean = true,
  chartsElement?: HTMLElement | null
): Promise<void> {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // Page 1: Top Header Brand Bar
  doc.setFillColor(11, 25, 44); // #0B192C dark slate
  doc.rect(0, 0, pageWidth, 75, 'F');

  doc.setTextColor(16, 185, 129); // #10B981 emerald
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('XNAV LEARNING ECOSYSTEM', 30, 32);

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(`Official Analytics Report: ${courseTitle}`, 30, 50);

  doc.setFontSize(8);
  doc.setTextColor(148, 163, 184);
  doc.text(`Generated: ${new Date().toLocaleDateString()} | Official Report`, pageWidth - 30, 50, { align: 'right' });

  let currentY = 90;

  if (withGraphs && summary) {
    // 1. KPI Summary Cards Strip
    const boxWidth = (pageWidth - 60 - 30) / 4;
    const kpis = [
      { label: 'TOTAL ENROLLED', val: `${summary.totalEnrolled || students.length}` },
      { label: 'ACTIVE STUDENTS', val: `${summary.activeStudents || students.filter(s => s.status === 'Active').length}` },
      { label: 'CLASS AVERAGE', val: `${summary.averageScore || 0}%` },
      { label: 'PASS RATE', val: `${summary.overallPassRate || 0}%` }
    ];

    kpis.forEach((kpi, idx) => {
      const x = 30 + idx * (boxWidth + 10);
      doc.setFillColor(248, 250, 252);
      doc.setDrawColor(226, 232, 240);
      doc.roundedRect(x, currentY, boxWidth, 42, 4, 4, 'FD');

      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(100, 116, 139);
      doc.text(kpi.label, x + 8, currentY + 14);

      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(16, 185, 129);
      doc.text(kpi.val, x + 8, currentY + 32);
    });

    currentY += 52;

    // 2. Structured 2x2 Vector Charts Grid (Page 1)
    const cardWidth = (pageWidth - 60 - 15) / 2;
    const cardHeight = 155;

    // Chart Box 1 (Top Left): Score Mastery Breakdown
    const card1X = 30;
    const card1Y = currentY;
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(226, 232, 240);
    doc.roundedRect(card1X, card1Y, cardWidth, cardHeight, 6, 6, 'FD');

    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text('Mastery Breakdown & Score Tiers', card1X + 12, card1Y + 18);

    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text(`Total Attempts: ${summary.totalAttempts || 0}`, card1X + 12, card1Y + 28);

    const masteryList = summary.masteryBreakdown || [
      { label: 'Mastery (90-100%)', count: 0, percentage: 0, color: '#10b981' },
      { label: 'Proficient (80-89%)', count: 0, percentage: 0, color: '#06b6d4' },
      { label: 'Passing (70-79%)', count: 0, percentage: 0, color: '#3b82f6' },
      { label: 'Needs Practice (<70%)', count: 0, percentage: 0, color: '#f59e0b' }
    ];

    masteryList.forEach((m, i) => {
      const rowY = card1Y + 44 + i * 26;
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(51, 65, 85);
      doc.text(m.label, card1X + 12, rowY);

      doc.setFontSize(7.5);
      doc.setTextColor(16, 185, 129);
      doc.text(`${m.count} (${m.percentage}%)`, card1X + cardWidth - 12, rowY, { align: 'right' });

      // Progress Track
      doc.setFillColor(241, 245, 249);
      doc.roundedRect(card1X + 12, rowY + 4, cardWidth - 24, 6, 3, 3, 'F');

      // Progress Fill
      const fillW = Math.max(((m.percentage || 0) / 100) * (cardWidth - 24), 2);
      if (i === 0) doc.setFillColor(16, 185, 129);
      else if (i === 1) doc.setFillColor(6, 182, 212);
      else if (i === 2) doc.setFillColor(59, 130, 246);
      else doc.setFillColor(245, 158, 11);
      doc.roundedRect(card1X + 12, rowY + 4, fillW, 6, 3, 3, 'F');
    });

    // Chart Box 2 (Top Right): Monthly Student Intake (2D Stream)
    const card2X = 30 + cardWidth + 15;
    const card2Y = currentY;
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(226, 232, 240);
    doc.roundedRect(card2X, card2Y, cardWidth, cardHeight, 6, 6, 'FD');

    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text('Monthly Student Intake & Growth', card2X + 12, card2Y + 18);

    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text(`Cumulative: ${students.length} Registered`, card2X + 12, card2Y + 28);

    const intakeList = summary.monthlyIntake || [];

    if (intakeList.length > 0) {
      const maxIntake = Math.max(...intakeList.map(d => d.enrolled), 1);
      const plotBottomY = card2Y + cardHeight - 24;
      const plotHeight = 75;

      // Grid baseline
      doc.setDrawColor(203, 213, 225);
      doc.line(card2X + 24, plotBottomY, card2X + cardWidth - 16, plotBottomY);

      const barW = Math.min((cardWidth - 48) / (intakeList.length * 2), 26);
      intakeList.forEach((d, i) => {
        const barH = Math.max((d.enrolled / maxIntake) * plotHeight, 4);
        const bX = card2X + 28 + (i + 0.5) * ((cardWidth - 56) / intakeList.length) - barW / 2;
        const bY = plotBottomY - barH;

        // 2D Bar
        doc.setFillColor(16, 185, 129);
        doc.roundedRect(bX, bY, barW, barH, 2, 2, 'F');

        // Value label on top
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(16, 185, 129);
        doc.text(`+${d.enrolled}`, bX + barW / 2, bY - 3, { align: 'center' });

        // Month label below
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 116, 139);
        doc.text(d.month, bX + barW / 2, plotBottomY + 12, { align: 'center' });
      });
    } else {
      doc.setFontSize(8);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(148, 163, 184);
      doc.text('No enrollment intake records available', card2X + cardWidth / 2, card2Y + cardHeight / 2, { align: 'center' });
    }

    currentY += cardHeight + 15;

    // Chart Box 3 (Bottom Left): Chapter & Quiz Pass Rates
    const card3X = 30;
    const card3Y = currentY;
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(226, 232, 240);
    doc.roundedRect(card3X, card3Y, cardWidth, cardHeight, 6, 6, 'FD');

    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text('Quiz & Chapter Pass Rates', card3X + 12, card3Y + 18);

    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text('Assessment performance by topic', card3X + 12, card3Y + 28);

    const chapterList = (summary.quizBreakdown || []).slice(0, 4);
    if (chapterList.length > 0) {
      chapterList.forEach((q, i) => {
        const rowY = card3Y + 44 + i * 26;
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(51, 65, 85);
        const titleTrimmed = q.title.length > 24 ? `${q.title.slice(0, 24)}...` : q.title;
        doc.text(titleTrimmed, card3X + 12, rowY);

        doc.setFontSize(7.5);
        doc.setTextColor(q.passRate >= 70 ? 16 : 245, q.passRate >= 70 ? 185 : 158, q.passRate >= 70 ? 129 : 11);
        doc.text(`${q.passRate}%`, card3X + cardWidth - 12, rowY, { align: 'right' });

        // Progress Track
        doc.setFillColor(241, 245, 249);
        doc.roundedRect(card3X + 12, rowY + 4, cardWidth - 24, 6, 3, 3, 'F');

        // Progress Fill
        const fillW = Math.max(((q.passRate || 0) / 100) * (cardWidth - 24), 2);
        doc.setFillColor(16, 185, 129);
        doc.roundedRect(card3X + 12, rowY + 4, fillW, 6, 3, 3, 'F');
      });
    } else {
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text('Course examination data active & monitored.', card3X + 12, card3Y + 70);
    }

    // Chart Box 4 (Bottom Right): Quiz Submissions Activity Timeline
    const card4X = 30 + cardWidth + 15;
    const card4Y = currentY;
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(226, 232, 240);
    doc.roundedRect(card4X, card4Y, cardWidth, cardHeight, 6, 6, 'FD');

    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text('Quiz Submissions Trend Curve', card4X + 12, card4Y + 18);

    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text('Past 14 Days Activity Timeline', card4X + 12, card4Y + 28);

    const timelineList = (summary.activityTimeline || []).slice(-8);
    const timeBottomY = card4Y + cardHeight - 24;
    const timeHeight = 75;

    // Grid baseline
    doc.setDrawColor(203, 213, 225);
    doc.line(card4X + 24, timeBottomY, card4X + cardWidth - 16, timeBottomY);

    if (timelineList.length > 1) {
      const maxAtt = Math.max(...timelineList.map(t => t.attempts), 4);
      const points = timelineList.map((t, i) => {
        const pX = card4X + 28 + (i / (timelineList.length - 1)) * (cardWidth - 56);
        const pY = timeBottomY - Math.max((t.attempts / maxAtt) * timeHeight, 4);
        return { pX, pY, date: t.date, attempts: t.attempts };
      });

      doc.setDrawColor(245, 158, 11);
      doc.setLineWidth(1.5);
      for (let i = 0; i < points.length - 1; i++) {
        doc.line(points[i].pX, points[i].pY, points[i + 1].pX, points[i + 1].pY);
      }

      points.forEach(p => {
        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(245, 158, 11);
        doc.circle(p.pX, p.pY, 3, 'FD');
      });

      // Date labels on first and last
      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(points[0].date.slice(5), points[0].pX, timeBottomY + 12, { align: 'center' });
      doc.text(points[points.length - 1].date.slice(5), points[points.length - 1].pX, timeBottomY + 12, { align: 'center' });
    }

    currentY += cardHeight + 20;

    // Notice at bottom of Page 1
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(100, 116, 139);
    doc.text(`* Detailed student performance roster continues on Page 2 (${students.length} total enrolled students).`, 30, currentY + 10);
  }

  // Page 2: Student Performance Roster Table
  doc.addPage();
  currentY = 40;

  // Header on Page 2
  doc.setFillColor(11, 25, 44);
  doc.roundedRect(30, currentY, pageWidth - 60, 32, 4, 4, 'F');

  doc.setTextColor(16, 185, 129);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('STUDENT PERFORMANCE ROSTER', 42, currentY + 20);

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.text(`${courseTitle} &bull; ${students.length} Enrolled Students`, pageWidth - 42, currentY + 20, { align: 'right' });

  currentY += 44;

  const tableData = students.map((s, idx) => [
    idx + 1,
    s.userName || 'Student',
    s.userEmail || '-',
    s.status || 'Active',
    s.quizAttemptsCount || 0,
    `${s.avgScorePercentage || 0}%`,
    `${s.highestScore || 0}%`,
    `${s.passRate || 0}%`,
    s.downloadedAt ? new Date(s.downloadedAt).toLocaleDateString() : '-'
  ]);

  autoTable(doc, {
    startY: currentY,
    head: [['#', 'Student Name', 'Email', 'Status', 'Attempts', 'Avg Score', 'High Score', 'Pass Rate', 'Enrolled Date']],
    body: tableData,
    theme: 'striped',
    headStyles: {
      fillColor: [11, 25, 44],
      textColor: [255, 255, 255],
      fontSize: 8,
      fontStyle: 'bold'
    },
    bodyStyles: {
      fontSize: 7.5,
      textColor: [30, 41, 59]
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252]
    },
    margin: { left: 30, right: 30 },
    styles: { overflow: 'linebreak', cellPadding: 4 }
  });

  // Reliable Blob File Download
  const pdfBlob = doc.output('blob');
  const blobUrl = URL.createObjectURL(pdfBlob);
  const cleanTitle = (courseTitle || 'Analytics').replace(/[^a-zA-Z0-9]/g, '_');
  const fileName = `xnav_report_${cleanTitle}_${withGraphs ? 'full_with_graphs' : 'roster_only'}_${new Date().toISOString().split('T')[0]}.pdf`;

  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 3000);
}

export function exportToCSV(students: EnrolledStudent[], courseTitle: string = 'Course'): void {
  const headers = [
    'Student Name',
    'Email',
    'User ID',
    'Course ID',
    'App Version',
    'Enrollment Date',
    'Status',
    'Quiz Attempts',
    'Avg Score %',
    'Highest Score %',
    'Pass Rate %',
    'Last Active'
  ];

  const rows = students.map(s => [
    `"${s.userName || ''}"`,
    `"${s.userEmail || ''}"`,
    `"${s.userId || ''}"`,
    `"${s.courseId || ''}"`,
    `"${s.appVersion || ''}"`,
    `"${s.downloadedAt ? new Date(s.downloadedAt).toLocaleDateString() : ''}"`,
    `"${s.status || 'Active'}"`,
    s.quizAttemptsCount || 0,
    `${s.avgScorePercentage || 0}%`,
    `${s.highestScore || 0}%`,
    `${s.passRate || 0}%`,
    `"${s.lastActive ? new Date(s.lastActive).toLocaleString() : ''}"`
  ]);

  const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
  const encodedUri = encodeURI(csvContent);
  const link = document.createElement('a');
  link.setAttribute('href', encodedUri);
  link.setAttribute('download', `xnav_student_analytics_${courseTitle.toLowerCase().replace(/[^a-z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function exportToJSON(data: any, courseTitle: string = 'Course'): void {
  const jsonContent = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(data, null, 2));
  const link = document.createElement('a');
  link.setAttribute('href', jsonContent);
  link.setAttribute('download', `xnav_analytics_${courseTitle.toLowerCase().replace(/[^a-z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.json`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
